import ActivationModel from "../ActivationModel";
import DebugOverlay, { type LKDebugCorner, type LKDebugInfo } from "./DebugOverlay";
import { getMotionOverlayEnabled } from "../debugSettings";
import { createZoneMask } from "../zoneMask";

// Standalone Lucas-Kanade motion detector, ported from the motion-test
// benchmark's "Lucas-Kanade feature tracking" algorithm. Tracks up to
// MAX_CORNERS Shi-Tomasi corners frame-to-frame with pyramidal LK optical
// flow and counts how many actually moved. Sensor noise and lighting drift
// change pixel values but do not move corners coherently, so a static scene
// stays near zero while a small or distant mover carries a handful of
// corners past the threshold.

const DETECTION_INTERVAL_MS = 250;
// Corners are cheap to track, so a higher input than the diff detector is affordable.
const MAX_INPUT_HEIGHT = 360;
const MAX_CORNERS = 200;
const QUALITY_LEVEL = 0.01;
const MIN_CORNER_DISTANCE = 8;
const LK_WINDOW_SIZE = 15;
const LK_PYRAMID_LEVELS = 2;
// Below this share of successfully tracked corners the frame content jumped
// (light switch, exposure step) — treat as a scene change, not motion.
const MIN_TRACK_RATIO = 0.5;
// Fewer trackable corners than this means a nearly featureless frame (dark or
// blank), which also covers the old detector's brightness check.
const MIN_TRACKABLE_CORNERS = 8;

// Consecutive active time required before motion arms, and quiet time after
// the last active frame before it releases.
const ARM_DURATION_MS = 750;
const RELEASE_DELAY_MS = 1000;

// displacementPx: distance (in pixels at 360p, per 250 ms step) a tracked
// corner must move to count as moving — corner tracking is sub-pixel precise,
// so values below 1 are meaningful. minMovingFeatures: moving corners a frame
// must exceed to be active. Values match the motion-test benchmark presets.
export const LucasKanadeSens = {
  HIGH: { displacementPx: 0.5, minMovingFeatures: 2 },
  MEDIUM: { displacementPx: 0.75, minMovingFeatures: 3 },
  LOW: { displacementPx: 1.5, minMovingFeatures: 6 }
} as const;

type LucasKanadeMotionDetectorConfig = {
  sensitivity?: MotionDetectionSensitivity;
  onChange?: (isMotion: boolean) => void;
};

type FrameResult = { movingFeatures: number; suppressed: boolean; debug?: LKDebugInfo };

export default class LucasKanadeMotionDetector {
  state: "idle" | "running" = "idle";
  isMotion = false;

  private video: HTMLVideoElement | null = null;
  private sensitivity: MotionDetectionSensitivity;
  private onChange?: (isMotion: boolean) => void;

  private cv: any = null;
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private grayFrame: any = null;
  private previousFrame: any = null;
  private winSize: any = null;
  private zoneMask: any = null;
  private gridMask: MotionDetectionZone["gridMask"] | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  private overlay: DebugOverlay | null = null;

  private model = new ActivationModel({
    armFrames: Math.max(1, Math.round(ARM_DURATION_MS / DETECTION_INTERVAL_MS)),
    releaseDelayMs: RELEASE_DELAY_MS
  });

  constructor({ sensitivity = "MEDIUM", onChange }: LucasKanadeMotionDetectorConfig) {
    this.sensitivity = sensitivity;
    this.onChange = onChange;
  }

  setSensitivity = (sensitivity: MotionDetectionSensitivity) => {
    log.motion("LK sensitivity changed:", sensitivity);
    this.sensitivity = sensitivity;
  };

  /** Pass null to detect over the whole frame. */
  setDetectionZone = (detectionZone: MotionDetectionZone | null) => {
    this.gridMask = detectionZone?.gridMask ?? null;
    this.zoneMask?.delete();
    this.zoneMask = null;
  };

  start = async (video: HTMLVideoElement) => {
    if (this.state === "running") return;
    this.video = video;

    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      log.motion("LK waiting for video 'loadeddata'...");
      video.addEventListener("loadeddata", () => this.start(video), { once: true });
      return;
    }

    this.state = "running";
    const cv = await window.cv;
    if (!cv) {
      log.warn("Couldn't start LK motion detector, OpenCV is not loaded");
      this.state = "idle";
      return;
    }
    if (this.state !== "running") return; // stopped while awaiting OpenCV

    log.info("About to start LK video processing for motion detection");
    this.cv = cv;
    this.canvas = document.createElement("canvas");
    this.context = this.canvas.getContext("2d", { willReadFrequently: true });
    this.grayFrame = new cv.Mat();
    this.winSize = new cv.Size(LK_WINDOW_SIZE, LK_WINDOW_SIZE);
    if (getMotionOverlayEnabled()) {
      this.overlay = new DebugOverlay();
      this.overlay.attach();
    }
    this.processVideo();
  };

  stop = () => {
    log.info("Stopping LK video processing for motion");
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.timeoutId = null;
    this.state = "idle";
    this.model.reset();
    this.isMotion = false;

    this.overlay?.detach();
    this.overlay = null;
    this.grayFrame?.delete();
    this.previousFrame?.delete();
    this.zoneMask?.delete();
    this.grayFrame = null;
    this.previousFrame = null;
    this.zoneMask = null;
    this.canvas = null;
    this.context = null;
  };

  private processVideo = () => {
    if (this.state !== "running") return;

    try {
      const frameStartMs = performance.now();
      const { movingFeatures, suppressed, debug } = this.detectFrame();
      const { minMovingFeatures } = LucasKanadeSens[this.sensitivity];
      const isMotion = this.model.update(movingFeatures, minMovingFeatures, performance.now(), suppressed);
      this.emitIfChanged(isMotion);
      if (debug) {
        const processingMs = performance.now() - frameStartMs;
        this.overlay?.render({ ...debug, movingFeatures, minMovingFeatures, suppressed, isMotion, processingMs });
      }
    } catch (err) {
      log.err("Failed LK 'processVideo'", err);
      this.model.reset();
      this.emitIfChanged(false);
    }

    this.timeoutId = setTimeout(this.processVideo, DETECTION_INTERVAL_MS);
  };

  private detectFrame = (): FrameResult => {
    const cv = this.cv;
    const frame = cv.matFromImageData(this.captureFrame());

    try {
      cv.cvtColor(frame, this.grayFrame, cv.COLOR_RGBA2GRAY);

      const sizeChanged =
        this.previousFrame &&
        (this.previousFrame.rows !== this.grayFrame.rows || this.previousFrame.cols !== this.grayFrame.cols);
      if (!this.previousFrame || sizeChanged) {
        this.previousFrame?.delete();
        this.previousFrame = this.grayFrame.clone();
        return { movingFeatures: 0, suppressed: true, debug: this.debugInfo([], 0, 0) };
      }

      return this.trackCorners();
    } finally {
      frame.delete();
    }
  };

  private trackCorners = (): FrameResult => {
    const cv = this.cv;
    const previousPoints = new cv.Mat();
    const nextPoints = new cv.Mat();
    const status = new cv.Mat();
    const error = new cv.Mat();

    try {
      const zoneMask = this.ensureZoneMask();
      if (zoneMask) {
        cv.goodFeaturesToTrack(
          this.previousFrame,
          previousPoints,
          MAX_CORNERS,
          QUALITY_LEVEL,
          MIN_CORNER_DISTANCE,
          zoneMask
        );
      } else {
        cv.goodFeaturesToTrack(this.previousFrame, previousPoints, MAX_CORNERS, QUALITY_LEVEL, MIN_CORNER_DISTANCE);
      }
      const cornerCount = previousPoints.rows;
      if (cornerCount < MIN_TRACKABLE_CORNERS) {
        this.grayFrame.copyTo(this.previousFrame);
        return { movingFeatures: 0, suppressed: true, debug: this.debugInfo([], 0, cornerCount) };
      }

      cv.calcOpticalFlowPyrLK(
        this.previousFrame,
        this.grayFrame,
        previousPoints,
        nextPoints,
        status,
        error,
        this.winSize,
        LK_PYRAMID_LEVELS
      );

      // The threshold is a speed: displacement per 250 ms reference step. A
      // longer detection interval lets the same speed travel further, so scale.
      const { displacementPx } = LucasKanadeSens[this.sensitivity];
      const displacementThreshold = displacementPx * (DETECTION_INTERVAL_MS / 250);

      let tracked = 0;
      let moving = 0;
      const corners: LKDebugCorner[] = [];
      const previousData = previousPoints.data32F;
      const nextData = nextPoints.data32F;
      const statusData = status.data;
      for (let index = 0; index < cornerCount; index += 1) {
        if (statusData[index] !== 1) continue;
        tracked += 1;
        const dx = nextData[index * 2] - previousData[index * 2];
        const dy = nextData[index * 2 + 1] - previousData[index * 2 + 1];
        const isMoving = dx * dx + dy * dy > displacementThreshold * displacementThreshold;
        if (isMoving) moving += 1;
        if (this.overlay)
          corners.push({ x: nextData[index * 2], y: nextData[index * 2 + 1], dx, dy, moving: isMoving });
      }

      this.grayFrame.copyTo(this.previousFrame);

      if (tracked / cornerCount < MIN_TRACK_RATIO) {
        log.motion("LK scene change, tracked", tracked, "of", cornerCount, "corners");
        return { movingFeatures: 0, suppressed: true, debug: this.debugInfo(corners, tracked, cornerCount) };
      }
      return { movingFeatures: moving, suppressed: false, debug: this.debugInfo(corners, tracked, cornerCount) };
    } finally {
      previousPoints.delete();
      nextPoints.delete();
      status.delete();
      error.delete();
    }
  };

  forceRest = () => {
    this.model.reset();
    this.emitIfChanged(false);
  };

  private captureFrame = () => {
    const video = this.video as HTMLVideoElement;
    const canvas = this.canvas as HTMLCanvasElement;
    const context = this.context as CanvasRenderingContext2D;

    const scale = Math.min(1, MAX_INPUT_HEIGHT / video.videoHeight);
    const width = Math.max(1, Math.round(video.videoWidth * scale));
    const height = Math.max(1, Math.round(video.videoHeight * scale));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    context.drawImage(video, 0, 0, width, height);
    return context.getImageData(0, 0, width, height);
  };

  // Corners are only searched inside the zone, so the mask lives in the LK
  // frame's coordinate space and rebuilds when the frame size changes.
  private ensureZoneMask = () => {
    if (!this.gridMask) return null;

    const { rows: frameRows, cols: frameCols } = this.grayFrame;
    if (this.zoneMask && this.zoneMask.rows === frameRows && this.zoneMask.cols === frameCols) {
      return this.zoneMask;
    }
    this.zoneMask?.delete();
    this.zoneMask = null;

    try {
      this.zoneMask = createZoneMask(this.cv, this.gridMask, frameRows, frameCols);
      log.motion("Created LK detection zone mask", frameCols, "x", frameRows);
    } catch (e) {
      log.err("Failed to create LK detection zone mask", e);
    }
    return this.zoneMask;
  };

  // Debug data is only collected while the overlay is attached.
  private debugInfo = (corners: LKDebugCorner[], tracked: number, total: number): LKDebugInfo | undefined => {
    if (!this.overlay) return undefined;
    return { width: this.grayFrame.cols, height: this.grayFrame.rows, corners, tracked, total };
  };

  private emitIfChanged = (isMotion: boolean) => {
    if (isMotion === this.isMotion) return;
    this.isMotion = isMotion;
    this.onChange?.(isMotion);
  };
}
